'use client';

import { useRouter } from 'next/navigation';

import { FormattedMessage } from '@flipster/i18n';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { square, stack } from '@flipster/styled-system/patterns';
import { convertWebviewToWebUrl } from '@flipster/utils/env-helper';
import webviewHandler from '@flipster/utils/webview-handler';

import {
  CarouselScrollContainer,
  ScrollIndicator,
  scrollTrackStyles,
  useCarouselScroll,
} from '../../misc/carousel-scroll';
import { useProductConfig } from '../../product-config';

const defaultLearnMorePath = '/blog/dynamic-earn-a-simpler-way-to-access-on-chain-yield';

const risks = [
  {
    title: <FormattedMessage id='Earn.r260224.Risks_item_1_title' />,
    desc: <FormattedMessage id='Earn.r260224.Risks_item_1_description' />,
    subDesc: <FormattedMessage id='Earn.r260224.Risks_item_1_subdescription' />,
  },
  {
    title: <FormattedMessage id='Earn.r260224.Risks_item_2_title' />,
    desc: <FormattedMessage id='Earn.r260224.Risks_item_2_description' />,
    subDesc: <FormattedMessage id='Earn.r260224.Risks_item_2_subdescription' />,
  },
  {
    title: <FormattedMessage id='Earn.r260224.Risks_item_3_title' />,
    desc: <FormattedMessage id='Earn.r260224.Risks_item_3_description' />,
    subDesc: <FormattedMessage id='Earn.r260224.Risks_item_3_subdescription' />,
  },
  {
    title: <FormattedMessage id='Earn.r260224.Risks_item_4_title' />,
    desc: <FormattedMessage id='Earn.r260224.Risks_item_4_description' />,
    subDesc: <FormattedMessage id='Earn.r260224.Risks_item_4_subdescription' />,
  },
] as const;

export const RisksTab = () => {
  const { learnMorePath } = useProductConfig();
  const router = useRouter();
  const { scrollRef, scrollProgress, handleScroll, handleProgressChange } = useCarouselScroll();
  const resolvedLearnMorePath = learnMorePath ?? defaultLearnMorePath;

  const handleLearnMore = () => {
    webviewHandler.postMessage(
      { action: 'external-link', params: { url: convertWebviewToWebUrl(resolvedLearnMorePath) } },
      () => router.push(resolvedLearnMorePath),
    );
  };

  return (
    <div className={stack({ gap: 16 })}>
      <CarouselScrollContainer progress={scrollProgress}>
        <div ref={scrollRef} onScroll={handleScroll} className={css(scrollTrackStyles, { gap: 12 })}>
          {risks.map((risk, idx) => (
            <div
              key={idx}
              className={stack({
                flexShrink: 0,
                w: 280,
                p: 20,
                gap: 16,
                rounded: 12,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-1',
                scrollSnapAlign: 'start',
              })}
            >
              <div className={stack({ gap: 4 })}>
                <p
                  className={css({
                    textStyle: 'Text/L/Strong',
                    color: 'content.default.level-5',
                  })}
                >
                  {risk.title}
                </p>
                <p
                  className={css({
                    textStyle: 'Text/M/Regular',
                    color: 'content.default.level-2',
                  })}
                >
                  {risk.desc}
                </p>
              </div>
              <div
                className={stack({
                  gap: 4,
                  px: 16,
                  pt: 12,
                  pb: 16,
                  borderColor: 'border.default.level-1',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  rounded: 8,
                  bg: 'background.default.level-1',
                })}
              >
                <p className={css({ textStyle: 'Text/L/Strong', color: 'content.accent.info' })}>
                  <FormattedMessage id='Earn.r260224.Risks_item_subtitle_common' />
                </p>
                <p
                  className={css({
                    textStyle: 'Text/M/Regular',
                    color: 'content.default.level-5',
                  })}
                >
                  {risk.subDesc}
                </p>
              </div>
            </div>
          ))}

          <button
            type='button'
            onClick={handleLearnMore}
            className={square({
              alignSelf: 'center',
              size: 120,
              rounded: 12,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-1',
              transition: 'background 0.2s',
              _hover: { bg: 'background.default.level-1' },
            })}
          >
            <div className={stack({ align: 'center', gap: 8 })}>
              <ArrowRightIcon className={css({ w: 20, h: 20, color: 'content.default.level-5' })} />
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage id='Common.r250401.Learn_more' />
              </p>
            </div>
          </button>
        </div>
      </CarouselScrollContainer>

      <ScrollIndicator progress={scrollProgress} onProgressChange={handleProgressChange} />
    </div>
  );
};
